import React, { useMemo } from 'react'
import Box from '@mui/material/Box'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import { SenderType } from '@finance-lab/utils/types'
import { useQuery, useUpdateQuery } from '../../../hooks/useQuery'
import { formatDateYYYYMMDD } from '../../../packages/utils/numberFormatters'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { PRESS_RELAESES_FILTER_PARAMS, validateSenderType, validateEndDate } from './utils'
import { endOfMonth } from 'date-fns'
import Autocomplete from '@mui/material/Autocomplete'
import Typography from '@mui/material/Typography'
import { debounce } from '@mui/material/utils'

const CONTENT_TYPE_LABELS = {
    showOnlyReports: 'Enbart rapporter',
    showOnlyWithRelatedTransactions: 'Enbart transaktioner',
} as const

export const PressReleasesFilter = ({
    isSlim,
    isSingleDaySelect,
}: {
    isSingleDaySelect?: boolean
    isSlim?: boolean
}) => {
    const query = useQuery()
    const updateQuery = useUpdateQuery()
    const showOnlyReports = query.get(PRESS_RELAESES_FILTER_PARAMS.SHOW_ONLY_REPORTS) === 'true'
    const showOnlyWithRelatedTransactions =
        query.get(PRESS_RELAESES_FILTER_PARAMS.SHOW_ONLY_WITH_RELATED_TRANSACTIONS) === 'true'
    const senderType = validateSenderType(query.get(PRESS_RELAESES_FILTER_PARAMS.SENDER_TYPE))
    const endDate = validateEndDate(query.get(PRESS_RELAESES_FILTER_PARAMS.END_DATE))

    const handleChangeEndDate = (value: Date | null) => {
        updateQuery(PRESS_RELAESES_FILTER_PARAMS.END_DATE, formatDateYYYYMMDD(value, true))
    }

    const handleChangeSenderType = (value: SenderType) => {
        updateQuery(PRESS_RELAESES_FILTER_PARAMS.SENDER_TYPE, value)
    }

    const handleChangeSearchValue = useMemo(
        () =>
            debounce((value: string) => {
                console.log({ value })
                updateQuery(PRESS_RELAESES_FILTER_PARAMS.SEARCH, value)
            }),
        [updateQuery],
    )

    const handleChangeContentType = (value: Array<keyof typeof CONTENT_TYPE_LABELS>) => {
        updateQuery(PRESS_RELAESES_FILTER_PARAMS.SHOW_ONLY_REPORTS, value.includes('showOnlyReports') ? 'true' : null)
        updateQuery(
            PRESS_RELAESES_FILTER_PARAMS.SHOW_ONLY_WITH_RELATED_TRANSACTIONS,
            value.includes('showOnlyWithRelatedTransactions') ? 'true' : null,
        )
    }

    const contentType = [
        showOnlyReports && 'showOnlyReports',
        showOnlyWithRelatedTransactions && 'showOnlyWithRelatedTransactions',
    ].filter((v) => !!v) as Array<keyof typeof CONTENT_TYPE_LABELS>

    return (
        <Grid container spacing={2}>
            <Grid size={6}>
                <Box sx={{ marginTop: 2, marginBottom: 4 }}>
                    <FormControl fullWidth>
                        <Autocomplete<keyof typeof CONTENT_TYPE_LABELS, true, true>
                            multiple
                            autoFocus
                            disableClearable
                            options={Object.keys(CONTENT_TYPE_LABELS) as (keyof typeof CONTENT_TYPE_LABELS)[]}
                            getOptionLabel={(v) => CONTENT_TYPE_LABELS[v]}
                            limitTags={1}
                            renderValue={(value) => {
                                const numTags = value.length

                                if (value.length > 1) return '2 valda'

                                return (
                                    <Typography variant='body2'>
                                        {value
                                            .slice(0, 1)
                                            .map((option) => CONTENT_TYPE_LABELS[option])
                                            .join(', ')}

                                        {numTags > 1 && ` +${numTags - 1}`}
                                    </Typography>
                                )
                            }}
                            renderInput={(params) => <TextField {...params} sx={{}} label='Innehåll' />}
                            onChange={(e, v) => handleChangeContentType(v)}
                            size={isSlim ? 'small' : undefined}
                            sx={{
                                width: '100%',
                                '.MuiInputBase-root': { cursor: 'pointer' },
                                '.MuiInputBase-input': { visibility: 'hidden' },
                            }}
                            value={contentType}
                        />
                    </FormControl>
                </Box>

                <Box sx={{ marginTop: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel id='sender-type' shrink>
                            Avsändare
                        </InputLabel>
                        <Select<SenderType>
                            id='sender-type'
                            label='Avsändare'
                            onChange={(v) => handleChangeSenderType(v.target.value)}
                            size={isSlim ? 'small' : undefined}
                            value={senderType}
                        >
                            <MenuItem value='company'>Bolaget</MenuItem>
                            <MenuItem value='Finansinspektionen'>Finansinspektionen</MenuItem>
                            <MenuItem value='analyze-and-ir'>Analys & IR</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Grid>

            <Grid size={6}>
                <Box sx={{ marginTop: 2, marginBottom: 4 }}>
                    <DatePicker
                        label={isSingleDaySelect ? 'Tidpunkt' : 'Tidsperiod slut'}
                        views={isSingleDaySelect ? ['year', 'month', 'day'] : ['year', 'month']}
                        format={'dd MMM yy'}
                        minDate={new Date('2015-01-01')}
                        maxDate={new Date()}
                        onChange={(newValue) => {
                            const d = newValue ?? new Date()

                            handleChangeEndDate(isSingleDaySelect ? d : endOfMonth(d))
                        }}
                        slotProps={{
                            textField: {
                                size: isSlim ? 'small' : undefined,
                            },
                        }}
                        value={new Date(endDate)}
                    />
                </Box>
                <Box>
                    <TextField
                        label='Fritextsökning'
                        size={isSlim ? 'small' : undefined}
                        onChange={(event) => handleChangeSearchValue(event.target.value)}
                    />
                </Box>
            </Grid>
        </Grid>
    )
}
